import { fsrs, generatorParameters, createEmptyCard, Rating, State } from 'ts-fsrs';

export { Rating, State };

export const RATINGS = [
  { value: Rating.Again, key: 'again', label: 'Forgot', hint: 'Show it again soon', hotkey: '1' },
  { value: Rating.Hard, key: 'hard', label: 'Hard', hint: 'Got it, with effort', hotkey: '2' },
  { value: Rating.Good, key: 'good', label: 'Good', hint: 'Remembered it', hotkey: '3' },
  { value: Rating.Easy, key: 'easy', label: 'Easy', hint: 'Knew it instantly', hotkey: '4' },
];

export const DEFAULT_DECK_CONFIG = {
  newPerDay: 20,
  reviewsPerDay: 200,
  retention: 0.9,
  maxInterval: 36500,
  learningSteps: '1m 10m',
  relearningSteps: '10m',
  fuzz: true,
  newOrder: 'mix',
};

export function resolveConfig(config) {
  return { ...DEFAULT_DECK_CONFIG, ...(config ?? {}) };
}

function steps(value, fallback) {
  const list = String(value ?? fallback)
    .split(/[\s,]+/)
    .filter((s) => /^\d+(\.\d+)?[smhd]$/.test(s));
  return list.length ? list : fallback.split(' ');
}

const schedulers = new Map();

export function schedulerFor(config) {
  const c = resolveConfig(config);
  const key = [c.retention, c.maxInterval, c.learningSteps, c.relearningSteps, c.fuzz].join('|');
  let s = schedulers.get(key);
  if (!s) {
    s = fsrs(
      generatorParameters({
        request_retention: Math.min(0.99, Math.max(0.7, Number(c.retention) || 0.9)),
        maximum_interval: Math.max(1, Number(c.maxInterval) || 36500),
        enable_fuzz: !!c.fuzz,
        enable_short_term: true,
        learning_steps: steps(c.learningSteps, '1m 10m'),
        relearning_steps: steps(c.relearningSteps, '10m'),
      }),
    );
    schedulers.set(key, s);
  }
  return s;
}

export function sortLogs(logs) {
  return logs
    .filter((l) => !l.deletedAt)
    .sort((a, b) => a.reviewedAt - b.reviewedAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

function toLocal(c) {
  return {
    due: c.due.getTime(),
    fsrs: {
      state: c.state,
      stability: c.stability,
      difficulty: c.difficulty,
      elapsedDays: c.elapsed_days,
      scheduledDays: c.scheduled_days,
      learningSteps: c.learning_steps,
      reps: c.reps,
      lapses: c.lapses,
      lastReview: c.last_review ? c.last_review.getTime() : null,
    },
  };
}

export function fromLocal(card) {
  const f = card.fsrs;
  if (!f) return createEmptyCard(new Date(card.createdAt ?? Date.now()));
  return {
    due: new Date(card.due),
    stability: f.stability,
    difficulty: f.difficulty,
    elapsed_days: f.elapsedDays,
    scheduled_days: f.scheduledDays,
    learning_steps: f.learningSteps ?? 0,
    reps: f.reps,
    lapses: f.lapses,
    state: f.state,
    last_review: f.lastReview ? new Date(f.lastReview) : undefined,
  };
}

/**
 * Derive a card's schedule purely from its review history. Deterministic, so every
 * device that holds the same logs computes the same schedule.
 */
export function replay(card, logs, config) {
  const s = schedulerFor(config);
  const ordered = sortLogs(logs);
  const start = Math.min(card.createdAt ?? Infinity, ordered[0]?.reviewedAt ?? Infinity);
  let c = createEmptyCard(new Date(Number.isFinite(start) ? start : 0));
  let firstReview = null;
  let lastRating = null;
  let lastReviewedAt = null;
  for (const log of ordered) {
    const at = new Date(log.reviewedAt);
    if (log.kind === 'reset') {
      c = createEmptyCard(at);
      firstReview = null;
      lastRating = null;
      continue;
    }
    if (!(log.rating >= 1 && log.rating <= 4)) continue;
    c = s.next(c, at, log.rating).card;
    firstReview ??= log.reviewedAt;
    lastRating = log.rating;
    lastReviewedAt = log.reviewedAt;
  }
  const local = toLocal(c);
  if (c.state === State.New) {
    // Canonical "new" form, identical to a freshly created card.
    local.due = card.createdAt ?? local.due;
    local.fsrs = null;
  }
  return { ...local, firstReview, lastRating, lastReviewedAt };
}

/** Interval (ms from now) for each rating if answered now. */
export function previewIntervals(card, config, now = Date.now()) {
  const s = schedulerFor(config);
  const rec = s.repeat(fromLocal(card), new Date(now));
  const out = {};
  for (const r of RATINGS) out[r.value] = Math.max(0, rec[r.value].card.due.getTime() - now);
  return out;
}

export function formatInterval(ms) {
  const min = ms / 60000;
  if (min < 1) return '<1m';
  if (min < 60) return `${Math.round(min)}m`;
  const h = min / 60;
  if (h < 24) return `${Math.round(h)}h`;
  const d = h / 24;
  if (d < 30) return `${Math.round(d)}d`;
  const mo = d / 30.4;
  if (mo < 12) return `${mo < 10 ? mo.toFixed(1).replace(/\.0$/, '') : Math.round(mo)}mo`;
  const y = d / 365;
  return `${y.toFixed(1).replace(/\.0$/, '')}y`;
}

export function cardStateName(card) {
  const st = card.fsrs?.state ?? State.New;
  if (card.suspended) return 'suspended';
  if (st === State.New) return 'new';
  if (st === State.Learning || st === State.Relearning) return 'learning';
  return 'review';
}
